import { createTestWorkerCommandClient, sendTestWorkerCommand, TEST_WORKER_URL } from './testWorkerClient';
import { kv } from './kv';

// A media message carries a few hundred KB (or MB) in its body — the default
// 15s timeout is not enough on a slow uplink, and retrying re-uploads the
// whole payload. Give media sends a long timeout and a single retry.
const mediaClient = createTestWorkerCommandClient({ timeoutMs: 60000, maxAttempts: 2 });

// "Delete chat for me" is per-device: we keep, per user, the timestamp each
// channel was cleared at and send it with every read so the Worker hides
// anything not newer (the docs and the other party stay untouched).
const clearedKey = (uid) => `chat:cleared:${uid}`;
const readCleared = async (uid) => {
  try { return JSON.parse((await kv.get(clearedKey(uid))) || '{}') || {}; } catch { return {}; }
};

// Chat runs through the worker (service account) for both reads and writes.
// Reads are polled; not instant, but reliable and RF-safe.
export function createChatClient(user) {
  const uid = user?.uid || 'anon';
  const authedGet = async (path, body) => {
    const token = await user.getIdToken();
    const res = await fetch(`${TEST_WORKER_URL}${path}`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ ...(body || {}), cleared: await readCleared(uid) }),
    });
    if (!res.ok) throw new Error(`chat ${path} ${res.status}`);
    return res.json();
  };

  return Object.freeze({
    ensure: () => sendTestWorkerCommand({ user, path: '/commands/chat-ensure', body: {}, idempotencyKey: `chat:${uid}:ensure:${Date.now()}` }),
    openDirect: (withUserId) => sendTestWorkerCommand({ user, path: '/commands/chat-open-direct', body: { withUserId }, idempotencyKey: `chat:${uid}:dm:${withUserId}:${Date.now()}` }),
    listDirectory: async () => (await authedGet('/chat/directory')).people || [],
    listChannels: async () => (await authedGet('/chat/channels')).channels || [],
    listMessages: async (channelId, afterId = null) => (await authedGet('/chat/messages', { channelId, afterId })).messages || [],
    createGroup: ({ title, memberIds }) => sendTestWorkerCommand({
      user,
      path: '/commands/chat-create-group',
      body: { title, memberIds },
      idempotencyKey: `chat:${uid}:group:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
    }),
    // Hide this chat for me from now — comes back if the other side writes again.
    clearChannel: async (channelId) => {
      const map = await readCleared(uid);
      map[channelId] = new Date().toISOString();
      await kv.set(clearedKey(uid), JSON.stringify(map));
    },
    send: ({ channelId, text, mediaType, mediaUrl, mediaName, mediaWidth, mediaHeight }) => {
      const request = {
        user,
        path: '/commands/chat-send',
        body: {
          channelId,
          text: text || null,
          mediaType: mediaType || null,
          mediaUrl: mediaUrl || null,
          mediaName: mediaName || null,
          mediaWidth: mediaWidth ?? null,
          mediaHeight: mediaHeight ?? null,
        },
        idempotencyKey: `chat:${uid}:send:${channelId}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      };
      return mediaUrl ? mediaClient.send(request) : sendTestWorkerCommand(request);
    },
  });
}
